import { useEffect, useState, useCallback } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { api } from "@/lib/api";

const fmtHour = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const barColor = (pct) => {
  if (pct >= 99) return "#34d399";
  if (pct >= 90) return "#fbbf24";
  return "#f87171";
};

export default function UptimeChart() {
  const [series, setSeries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/monitor/series?hours=48", { timeout: 20000 }),
        api.get("/monitor/uptime", { timeout: 20000 }),
      ]);
      setSeries(
        (s.data || []).map((p) => ({
          ...p,
          time: fmtHour(p.t),
          latency_ms: p.latency_ms ?? 0,
        }))
      );
      setSummary(u.data);
    } catch (e) {
      if (e?.response?.status !== 401) console.error("uptime fetch error", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [fetchData]);

  const downCount = series.filter((p) => !p.up).length;

  return (
    <section className="card" data-testid="uptime-section">
      <div className="card-head">
        <h2>📈 التوفر وزمن الاستجابة</h2>
        <span className="chip">تحديث تلقائي كل دقيقة</span>
      </div>

      {loading && (
        <div className="monitor-loading"><span className="spinner big" /> جاري تحميل الإحصائيات...</div>
      )}

      {!loading && summary && (
        <>
          <div className="grid stats-grid" data-testid="uptime-stats">
            <div className="stat-card">
              <div className="stat-value c-ok" data-testid="uptime-24h">
                {summary.uptime_24h != null ? `${summary.uptime_24h}%` : "—"}
              </div>
              <div className="stat-label">التوفر آخر 24 ساعة</div>
            </div>
            <div className="stat-card">
              <div className="stat-value c-ok" data-testid="uptime-7d">
                {summary.uptime_7d != null ? `${summary.uptime_7d}%` : "—"}
              </div>
              <div className="stat-label">التوفر آخر 7 أيام</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" data-testid="avg-latency">
                {summary.avg_latency_ms != null ? `${summary.avg_latency_ms}ms` : "—"}
              </div>
              <div className="stat-label">متوسط زمن الاستجابة</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" data-testid="total-checks">{summary.total_checks}</div>
              <div className="stat-label">إجمالي الفحوصات</div>
            </div>
          </div>

          <h3 className="chart-title">زمن الاستجابة (ms) — آخر 48 ساعة {downCount > 0 && <span className="c-fail">({downCount} فحص فاشل)</span>}</h3>
          <div className="chart-wrap" dir="ltr" data-testid="latency-chart">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={series} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="latGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,190,255,0.08)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#93a3bf" }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10, fill: "#93a3bf" }} />
                <Tooltip
                  contentStyle={{ background: "#0d1420", border: "1px solid rgba(148,190,255,0.2)", borderRadius: 10, color: "#e8eefb", fontSize: 12 }}
                  formatter={(v, name) => [`${v} ms`, name === "latency_ms" ? "HTTP" : name]}
                />
                <Area type="monotone" dataKey="latency_ms" stroke="#22d3ee" strokeWidth={2} fill="url(#latGrad)" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {summary.per_day?.length > 0 && (
            <>
              <h3 className="chart-title">نسبة التوفر اليومية (%)</h3>
              <div className="chart-wrap" dir="ltr" data-testid="daily-uptime-chart">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={summary.per_day} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,190,255,0.08)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#93a3bf" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#93a3bf" }} />
                    <Tooltip
                      contentStyle={{ background: "#0d1420", border: "1px solid rgba(148,190,255,0.2)", borderRadius: 10, color: "#e8eefb", fontSize: 12 }}
                      formatter={(v, name, props) => [`${v}% (${props?.payload?.checks} فحص)`, "التوفر"]}
                    />
                    <Bar dataKey="uptime_pct" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {summary.per_day.map((d, i) => (
                        <Cell key={i} fill={barColor(d.uptime_pct)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
